/**
 * No Permissions Page
 * Shown when an employee has no permissions assigned
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from '@mui/material';
import {
  Lock as LockIcon,
  ExitToApp as LogoutIcon,
  ContactSupport as ContactIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { logout } from '../../store/slices/authSlice';
import { palette } from '../../theme/palette';
import toast from 'react-hot-toast';

const NoPermissionsPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleCopyEmail = () => {
    const adminEmail = 'admin@hcs-system.com'; // Replace with actual admin email
    navigator.clipboard.writeText(adminEmail);
    toast.success('Email copied to clipboard');
  };

  const handleCopyPhone = () => {
    const adminPhone = '+20 123 456 7890'; // Replace with actual admin phone
    navigator.clipboard.writeText(adminPhone);
    toast.success('Phone number copied to clipboard');
  };

  const handleEmailAdmin = () => {
    const adminEmail = 'admin@hcs-system.com'; // Replace with actual admin email
    const subject = encodeURIComponent('Request for Dashboard Permissions');
    const body = encodeURIComponent(
      `Hello Administrator,\n\n` +
      `I am ${user?.full_name} (Employee ID: ${user?.employee_profile?.employee_id}) and I need permissions assigned to access the dashboard.\n\n` +
      `My Details:\n` +
      `- Department: ${user?.employee_profile?.department}\n` +
      `- Position: ${user?.employee_profile?.position}\n` +
      `- Role: ${user?.employee_profile?.role?.name || 'N/A'}\n\n` +
      `Please assign the necessary permissions to my account.\n\n` +
      `Thank you.`
    );
    window.location.href = `mailto:${adminEmail}?subject=${subject}&body=${body}`;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: palette.light.background,
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Card
          sx={{
            textAlign: 'center',
            py: 6,
            px: 4,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          }}
        >
          <CardContent>
            {/* Icon */}
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                backgroundColor: palette.status.warning + '20',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <LockIcon
                sx={{
                  fontSize: 60,
                  color: palette.status.warning,
                }}
              />
            </Box>

            {/* Title */}
            <Typography variant="h4" fontWeight={700} gutterBottom>
              No Permissions Assigned
            </Typography>

            {/* User Info */}
            {user && (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 3 }}
              >
                Welcome, <strong>{user.full_name}</strong>
              </Typography>
            )}

            {/* Description */}
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 2, lineHeight: 1.8 }}
            >
              Your account has been created successfully, but you currently don't have
              any permissions assigned to access the dashboard.
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 4, lineHeight: 1.8 }}
            >
              Please contact your system administrator to assign the appropriate
              permissions to your account.
            </Typography>

            {/* Employee Info */}
            {user?.employee_profile && (
              <Box
                sx={{
                  backgroundColor: palette.light.muted,
                  borderRadius: 2,
                  p: 2,
                  mb: 4,
                  textAlign: 'left',
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Your Employee Information:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Employee ID:
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {user.employee_profile.employee_id}
                    </Typography>
                  </Box>
                  {user.employee_profile.role && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Role:
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {user.employee_profile.role.name}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Department:
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {user.employee_profile.department}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Position:
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {user.employee_profile.position}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Actions */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                mt: 4,
              }}
            >
              <Button
                variant="outlined"
                size="large"
                startIcon={<ContactIcon />}
                onClick={() => setContactDialogOpen(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Contact Administrator
              </Button>
              <Button
                variant="contained"
                size="large"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Logout
              </Button>
            </Box>

            {/* Help Text */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 4 }}
            >
              If you believe this is an error, please contact your system administrator
              or IT support team.
            </Typography>
          </CardContent>
        </Card>
      </Container>

      {/* Contact Administrator Dialog */}
      <Dialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight={600}>
              Contact Administrator
            </Typography>
            <IconButton
              size="small"
              onClick={() => setContactDialogOpen(false)}
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Use any of the following methods to contact the system administrator:
          </Typography>

          <List>
            {/* Email */}
            <ListItem
              sx={{
                bgcolor: palette.light.muted,
                borderRadius: 1,
                mb: 2,
              }}
            >
              <ListItemIcon>
                <EmailIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Email"
                secondary="admin@hcs-system.com"
                primaryTypographyProps={{ fontWeight: 600 }}
              />
              <IconButton
                size="small"
                onClick={handleCopyEmail}
                sx={{ mr: 1 }}
                title="Copy email"
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </ListItem>

            {/* Phone */}
            <ListItem
              sx={{
                bgcolor: palette.light.muted,
                borderRadius: 1,
                mb: 2,
              }}
            >
              <ListItemIcon>
                <PhoneIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Phone"
                secondary="+20 123 456 7890"
                primaryTypographyProps={{ fontWeight: 600 }}
              />
              <IconButton
                size="small"
                onClick={handleCopyPhone}
                title="Copy phone number"
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </ListItem>
          </List>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Please include your Employee ID ({user?.employee_profile?.employee_id}) when
            contacting the administrator.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setContactDialogOpen(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<EmailIcon />}
            onClick={handleEmailAdmin}
          >
            Send Email
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NoPermissionsPage;

